/* eslint-disable @typescript-eslint/no-explicit-any -- react-hook-form useFieldArray/Control types require any for array field names */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { nanoApi } from '@/services/learningManagementApi';
import { skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import type {
  Nano,
  NanoRequest,
  TipCategory,
  TipType,
  TipDifficultyLevel,
  TipImpactLevel,
  GenerationSource,
  TargetAudience,
} from '@/types/learningManagement';

const nanoSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
  skillId: z.string().optional(),
  description: z.string().max(2000, 'Description must be less than 2000 characters').optional(),
  content: z.string().min(1, 'Content is required').max(5000, 'Content must be less than 5000 characters'), // Required by API
  summary: z.string().max(500, 'Summary must be less than 500 characters').optional(),
  category: z.enum(['DAILY_TIP', 'WEEKLY_TIP', 'MONTHLY_TIP', 'QUICK_ADVICE', 'IN_DEPTH_GUIDE', 'CHECKLIST', 'BEST_PRACTICES', 'TIPS_AND_TRICKS']),
  tipType: z.enum(['PRODUCTIVITY', 'LEARNING_STRATEGY', 'GOAL_ACHIEVEMENT', 'TIME_MANAGEMENT', 'HABIT_FORMATION', 'SKILL_DEVELOPMENT', 'MOTIVATION', 'WELLNESS', 'CAREER_DEVELOPMENT', 'PROBLEM_SOLVING']),
  difficultyLevel: z.enum(['EASY', 'MODERATE', 'CHALLENGING', 'ADVANCED']),
  status: z.enum(['DRAFT', 'ACTIVE', 'SCHEDULED', 'PAUSED', 'ARCHIVED', 'EXPIRED', 'COMPLETED', 'IN_PROGRESS', 'NOT_STARTED']).optional(),
  impactLevel: z.enum(['LOW', 'MEDIUM', 'HIGH', 'TRANSFORMATIVE']).optional(),
  implementationTimeMinutes: z.number().min(1).max(1440).optional(),
  resources: z.array(z.string()),
  targetAudience: z.enum(['ALL_USERS', 'BEGINNERS', 'INTERMEDIATE', 'ADVANCED', 'PROFESSIONALS', 'STUDENTS', 'MANAGERS', 'CUSTOM_SEGMENT']).optional(),
  tags: z.array(z.string()).optional(),
  nanoGenerated: z.enum(['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED']),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

type NanoFormData = z.infer<typeof nanoSchema>;

const CATEGORIES: TipCategory[] = ['DAILY_TIP', 'WEEKLY_TIP', 'MONTHLY_TIP', 'QUICK_ADVICE', 'IN_DEPTH_GUIDE', 'CHECKLIST', 'BEST_PRACTICES', 'TIPS_AND_TRICKS'];
const TIP_TYPES: TipType[] = ['PRODUCTIVITY', 'LEARNING_STRATEGY', 'GOAL_ACHIEVEMENT', 'TIME_MANAGEMENT', 'HABIT_FORMATION', 'SKILL_DEVELOPMENT', 'MOTIVATION', 'WELLNESS', 'CAREER_DEVELOPMENT', 'PROBLEM_SOLVING'];
const DIFFICULTY_LEVELS: TipDifficultyLevel[] = ['EASY', 'MODERATE', 'CHALLENGING', 'ADVANCED'];
const IMPACT_LEVELS: TipImpactLevel[] = ['LOW', 'MEDIUM', 'HIGH', 'TRANSFORMATIVE'];
const TARGET_AUDIENCES: TargetAudience[] = ['ALL_USERS', 'BEGINNERS', 'INTERMEDIATE', 'ADVANCED', 'PROFESSIONALS', 'STUDENTS', 'MANAGERS', 'CUSTOM_SEGMENT'];
const GENERATION_SOURCES: GenerationSource[] = ['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED'];

export const NanoFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [skills, setSkills] = useState<Array<{ id?: string; name: string }>>([]);

  const form = useForm<NanoFormData>({
    resolver: zodResolver(nanoSchema) as any,
    defaultValues: {
      title: '',
      skillId: '',
      description: '',
      content: '',
      summary: '',
      category: 'DAILY_TIP',
      tipType: 'PRODUCTIVITY',
      difficultyLevel: 'EASY',
      status: 'DRAFT',
      impactLevel: undefined,
      implementationTimeMinutes: undefined,
      resources: [],
      targetAudience: undefined,
      tags: [],
      nanoGenerated: 'USER_CUSTOMIZED',
    },
  });

  const { fields: resourceFields, append: appendResource, remove: removeResource } = useFieldArray({
    control: form.control as any,
    name: 'resources' as any,
  });

  useEffect(() => {
    skillApi.list({ page: 0, size: 1000 }).then((response) => {
      setSkills(response.content || []);
    }).catch((error) => {
      console.error('Error loading skills:', error);
    });
  }, []);

  // Fetch nano data for edit mode
  const {
    data: _nano,
    isLoading: isLoadingNano,
    error: nanoError,
  } = useEntity<Nano>({
    fetchFn: (nanoId: string) => nanoApi.getById(nanoId),
    entityId: id,
    entityName: 'nano',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        title: data.title,
        skillId: data.skillId || '',
        description: data.description || '',
        content: data.content || '',
        summary: data.summary || '',
        category: data.category,
        tipType: data.tipType,
        difficultyLevel: data.difficultyLevel,
        status: data.status,
        impactLevel: data.impactLevel,
        implementationTimeMinutes: data.implementationTimeMinutes,
        resources: data.resources || [],
        targetAudience: data.targetAudience,
        tags: data.tags || [],
        nanoGenerated: data.nanoGenerated,
      } as any);
    },
    onError: () => {
      navigate('/admin/nanos');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: NanoFormData) => {
      const payload: NanoRequest = {
        ...data,
        skillId: data.skillId === 'none' || !data.skillId ? undefined : data.skillId,
        status: data.status || 'DRAFT',
        resources: data.resources || [],
      } as NanoRequest;
      return await nanoApi.create(payload);
    },
    entityName: 'nano',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/nanos');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: NanoFormData) => {
      if (!id) throw new Error('Nano ID is required');
      const payload: NanoRequest = {
        ...data,
        skillId: data.skillId === 'none' || !data.skillId ? undefined : data.skillId,
        status: data.status || 'DRAFT',
        resources: data.resources || [],
      } as NanoRequest;
      return await nanoApi.update(id, payload);
    },
    entityName: 'nano',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/nanos');
    },
  });

  const onSubmit = async (data: NanoFormData) => {
    try {
      if (isEdit) {
        await updateMutation.mutateAsync(data);
      } else {
        await createMutation.mutateAsync(data);
      }
    } catch (error) {
      console.error('Error submitting nano form:', error);
    }
  };

  form.handleSubmit(onSubmit, (errors) => {
    console.error('Form validation errors:', errors);
  });

  const isLoading = isLoadingNano;
  const mutation = isEdit ? updateMutation : createMutation;

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading nano..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && nanoError) {
    return <ErrorState error={nanoError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/nanos">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Nano' : 'Create Nano'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update nano information' : 'Create a new nano'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
          console.error('Form validation errors:', errors);
        })} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for the nano</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter nano title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter nano description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter nano content"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="summary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Summary</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter nano summary" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="skillId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Skill</FormLabel>
                    <Select 
                      onValueChange={(value) => field.onChange(value === 'none' ? undefined : value)} 
                      value={field.value || 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select skill (optional)" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {skills.map((skill) => (
                          <SelectItem key={skill.id} value={skill.id || ''}>
                            {skill.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tipType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tip Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select tip type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TIP_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="difficultyLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Difficulty Level *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select difficulty" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {DIFFICULTY_LEVELS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {level}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="impactLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Impact Level</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select impact level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {IMPACT_LEVELS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {level}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="targetAudience"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Target Audience</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select target audience" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TARGET_AUDIENCES.map((audience) => (
                            <SelectItem key={audience} value={audience}>
                              {audience.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="implementationTimeMinutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Implementation Time (Minutes)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        placeholder="Enter minutes"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <Label>Resources</Label>
                <div className="space-y-2">
                  {resourceFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                      <Input
                        placeholder="Enter resource URL or description"
                        value={form.watch(`resources.${index}`) || ''}
                        onChange={(e) => {
                          const current = form.getValues('resources') || [];
                          current[index] = e.target.value;
                          form.setValue('resources', current);
                        }}
                      />
                      <Button type="button" variant="outline" size="icon" onClick={() => removeResource(index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => appendResource('')}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Resource
                  </Button>
                </div>
              </div>

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter tags separated by commas"
                        value={field.value?.join(', ') || ''}
                        onChange={(e) => {
                          const tags = e.target.value
                            .split(',')
                            .map((t) => t.trim())
                            .filter(Boolean);
                          field.onChange(tags);
                        }}
                      />
                    </FormControl>
                    <FormDescription>Separate multiple tags with commas</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="nanoGenerated"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Generation Source *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select generation source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {GENERATION_SOURCES.map((source) => (
                          <SelectItem key={source} value={source}>
                            {source.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/nanos">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default NanoFormPage;
